import { apiFetch, qs } from '../lib/api';

export type TireVendor = 'ATD' | 'WheelPros' | 'Manual';
export type TireSourceKind = 'atd_soap' | 'csv_upload' | 'json_upload';
export type TireJobStatus = 'pending' | 'processing' | 'preview' | 'applied' | 'failed';
export type TireDisposition = 'new' | 'update' | 'duplicate' | 'invalid' | 'toyo_skipped';
export type InstallSpeed = 'same_day' | 'next_day' | 'standard' | 'estimated' | 'out_of_stock';

export interface TireImportJob {
  id: number;
  vendor: TireVendor;
  source_kind: TireSourceKind;
  status: TireJobStatus;
  filename: string | null;
  s3_key: string | null;
  product_groups: string[];
  brands: string[];
  raw_row_count: number;
  valid_count: number;
  skipped_count: number;
  toyo_skipped_count: number;
  needs_review_count: number;
  inserted_count: number;
  updated_count: number;
  error: string | null;
  uploaded_by: string | null;
  dry_run: boolean;
  created_at: string;
  applied_at: string | null;
}

export interface TireImportJobDetail {
  job: TireImportJob;
  dispositions: Array<{ disposition: TireDisposition; n: number }>;
  samples: Record<TireDisposition, Array<{
    row_index: number;
    parsed_payload: Record<string, unknown> | null;
    errors: string[];
    warnings: string[];
  }>>;
}

export interface TireProduct {
  id: number;
  vendor: TireVendor;
  vendor_sku: string;
  brand: string;
  model: string;
  tire_size_full: string;
  size_display: string | null;
  terrain: string | null;
  season: string | null;
  vehicle_type: string | null;
  install_speed: InstallSpeed;
  qty_local: number | null;
  qty_local_plus: number | null;
  qty_nationwide: number | null;
  price_cents: number;
  msrp_cents: number | null;
  needs_review: boolean;
  needs_review_reasons: string[];
  imported_at: string;
  updated_at: string;
}

export interface CreateTireJobPayload {
  vendor: TireVendor;
  source_kind: TireSourceKind;
  product_groups?: string[];
  brands?: string[];
  s3_key?: string;
  filename?: string;
  dry_run?: boolean;
}

export function listTireJobs(): Promise<{ data: TireImportJob[] }> {
  return apiFetch('/admin/tires/jobs');
}

export function getTireJob(id: number): Promise<TireImportJobDetail> {
  return apiFetch(`/admin/tires/jobs/${id}`);
}

export function createTireJob(body: CreateTireJobPayload): Promise<{ id: number; status: TireJobStatus }> {
  return apiFetch('/admin/tires/jobs', { method: 'POST', body: JSON.stringify(body) });
}

export function applyTireJob(id: number): Promise<{ inserted: number; updated: number; skipped: number }> {
  return apiFetch(`/admin/tires/jobs/${id}/apply`, { method: 'POST' });
}

export type TireSortBy =
  | 'sku' | 'brand' | 'model' | 'size' | 'terrain'
  | 'vehicle' | 'install' | 'price' | 'qty' | 'updated';

export interface TireListParams {
  // Multi-select filters — arrays serialize as repeated ?brand=foo&brand=bar.
  vendor?: TireVendor[];
  brand?: string[];
  terrain?: string[];
  vehicle_type?: string[];
  install_speed?: InstallSpeed[];
  // Range
  diameter_min?: number;
  diameter_max?: number;
  // Single-value
  q?: string;
  needs_review?: boolean;
  // Sort
  sort_by?: TireSortBy;
  sort_dir?: 'asc' | 'desc';
  // Pagination
  limit?: number;
  offset?: number;
}

export function listTires(params: TireListParams = {}): Promise<{
  data: TireProduct[];
  pagination: { total: number; limit: number; offset: number };
}> {
  return apiFetch(`/admin/tires${qs(params as Record<string, unknown>)}`);
}

export interface TireStats {
  total_active: number;
  by_vendor: { ATD: number; WheelPros: number; Manual: number };
  by_install_speed: Record<InstallSpeed, number>;
  needs_review: number;
  // Go-Live tiles — added 2026-04-29 for the launch dashboard.
  go_live_ready: number;        // shopify_ready_status in (ready, warning) AND in stock
  already_pushed: number;       // shopify_product_id IS NOT NULL
  pending_push: number;         // go_live_ready - already_pushed (clamped >= 0)
  // Brand list with row counts — populates the admin Brand multi-select
  // filter on the tires page. Capped at 200 brands server-side.
  brands: Array<{ brand: string; count: number }>;
}

export function getTireStats(): Promise<TireStats> {
  return apiFetch('/admin/tires/stats');
}

export type ClassificationSource =
  | 'atd' | 'wheelpros' | 'inferred_model' | 'inferred_pattern'
  | 'inferred_category' | 'inferred_size' | 'admin';
export type DescriptionSource =
  | 'manufacturer' | 'structured_facts' | 'admin' | 'blank';

// Full tire row (every column on tire_products) for the detail page.
export interface TireProductFull extends TireProduct {
  brand_slug: string;
  handle: string;
  size_format: 'metric' | 'flotation';
  rim_diameter: number;
  section_width: number | null;
  aspect_ratio: number | null;
  overall_diameter: number | null;
  load_range: string | null;
  ply_rating: number | null;
  load_index: string | null;
  speed_rating: string | null;
  run_flat: boolean | null;
  studdable: boolean | null;
  sidewall: string | null;
  cost_cents: number | null;
  fet_cents: number | null;
  image_url: string | null;
  upc: string | null;
  ai_description_html: string | null;
  ai_description_generated_at: string | null;
  ai_description_model: string | null;
  ai_description_prompt_hash: string | null;
  raw_payload: unknown | null;
  is_active: boolean;
  source_job_id: number | null;

  // Migration 022 additions.
  mfg_part_number: string | null;
  atd_description: string | null;
  mileage_warranty: string | null;
  tread_wear: number | null;
  tread_depth: number | null;
  overall_tire_diameter: number | null;
  terrain_source: ClassificationSource | null;
  vehicle_type_source: ClassificationSource | null;
  shopify_title: string | null;
  shopify_handle: string | null;
  seo_title: string | null;
  seo_description: string | null;
  product_description: string | null;
  description_source: DescriptionSource | null;
  product_tags: string[];
  collection_tags: string[];
  metafields_json: Record<string, unknown> | null;

  // Migration 023 additions — full ATD specs.
  canadian_mileage_warranty: string | null;
  traction_rating: string | null;
  temperature_rating: string | null;
  utqg: string | null;
  max_psi: number | null;
  max_load_lbs: number | null;
  revolutions_per_mile: number | null;
  shipping_weight_lbs: number | null;
  min_rim_width: number | null;
  max_rim_width: number | null;
  radial_bias: 'R' | 'B' | null;
  country_of_origin: string | null;
  atd_short_description: string | null;
  atd_long_description: string | null;
  image_gallery_json: { large: string[]; small: string[]; thumbnail: string[] } | null;

  // Migration 031 additions — Wheel Pros enrichment layer.
  vendor_description: string | null;          // Wheel Pros narrative copy
  enriched_by_vendors: string[];               // ['ATD','WheelPros'] etc.
  map_price_cents: number | null;              // Wheel Pros MAP floor
  manufacturer_description: string | null;     // admin-curated (mig 025)
  manufacturer_features_json: string[] | null; // admin-curated (mig 025)
  manufacturer_warranty_text: string | null;   // admin-curated (mig 025)
  manufacturer_source_url: string | null;      // admin-curated (mig 025)
  inventory_source: string | null;             // mig 025
  size_display: string | null;                 // mig 025+026

  // Migration 038 — push-gate verdict persisted alongside the listing
  // fields. Read-only on the detail page; written by the push-gate audit.
  shopify_ready_status: 'ready' | 'warning' | 'blocked' | 'excluded' | null;
  shopify_ready_reasons: { blocking_reasons?: string[]; warnings?: string[] } | null;
  hs_code: string | null;
  shopify_product_id: number | null;
  google_product_category?: string | null;
  product_type?: string | null;
}

export interface TirePatch {
  terrain?: 'mt' | 'rt' | 'at' | 'ht' | null;
  vehicle_type?: 'truck' | 'suv' | 'car' | 'offroad' | null;
  season?: 'all-season' | 'summer' | 'winter' | null;
  sidewall?: string | null;
  run_flat?: boolean | null;
  studdable?: boolean | null;
  image_url?: string | null;
  ai_description_html?: string | null;
  is_active?: boolean;
  needs_review?: boolean;
}

export function getTire(id: number): Promise<TireProductFull> {
  return apiFetch(`/admin/tires/${id}`);
}

export function updateTire(id: number, patch: TirePatch): Promise<TireProductFull> {
  return apiFetch(`/admin/tires/${id}`, { method: 'PATCH', body: JSON.stringify(patch) });
}

export interface TireVariantSibling {
  id: number;
  sku: string;
  brand: string | null;
  model: string | null;
  tire_size_full: string | null;
  size_display: string | null;
  section_width: number | null;
  aspect_ratio: number | null;
  rim_diameter: number | null;
  load_index: string | null;
  speed_rating: string | null;
  load_range: string | null;
  sidewall: string | null;
  price_cents: number | null;
  msrp_cents: number | null;
  map_price_cents: number | null;
  qty_local: number | null;
  qty_local_plus: number | null;
  qty_nationwide: number | null;
  install_speed: InstallSpeed | null;
  image_url: string | null;
  shopify_ready_status: string | null;
  shopify_product_id: number | null;
  is_active: boolean;
}

export function getTireVariants(id: number): Promise<{ variants: TireVariantSibling[] }> {
  return apiFetch(`/admin/tires/${id}/variants`);
}
